import type { GlassArea } from '~/lib/api';

/**
 * The words a person standing at a vehicle would use. The enum values are machine
 * codes and never appear on screen — nobody says DRIVER_SIDE_QUARTER_GLASS.
 *
 * Shared, because the vehicle diagram, the selected chips, the shoot header and the
 * review groups all need the same label for the same area, and three copies would
 * drift.
 */
export const AREA_LABELS: Record<GlassArea, string> = {
  FRONT_WINDSHIELD: 'windscreen',
  REAR_WINDSHIELD: 'rear screen',
  DRIVER_SIDE_FRONT_WINDOW: 'driver front',
  PASSENGER_SIDE_FRONT_WINDOW: 'passenger front',
  DRIVER_SIDE_REAR_WINDOW: 'driver rear',
  PASSENGER_SIDE_REAR_WINDOW: 'passenger rear',
  DRIVER_SIDE_QUARTER_GLASS: 'driver quarter glass',
  PASSENGER_SIDE_QUARTER_GLASS: 'passenger quarter glass',
  ROOF_GLASS: 'roof / sunroof',
};

/**
 * The same label as a TITLE — sentence case, as the frames set it wherever an area heads
 * its own block. The chips on 03 and the shoot header stay lower case, which is also the
 * frames. Here rather than inline at each call site, because it was already written twice.
 */
export function areaTitle(area: GlassArea): string {
  const label = AREA_LABELS[area];
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Order an operator walks the car: front, sides front-to-back, roof, rear. */
export const AREAS: GlassArea[] = [
  'FRONT_WINDSHIELD',
  'DRIVER_SIDE_FRONT_WINDOW',
  'PASSENGER_SIDE_FRONT_WINDOW',
  'DRIVER_SIDE_REAR_WINDOW',
  'PASSENGER_SIDE_REAR_WINDOW',
  'DRIVER_SIDE_QUARTER_GLASS',
  'PASSENGER_SIDE_QUARTER_GLASS',
  'ROOF_GLASS',
  'REAR_WINDSHIELD',
];

/**
 * The areas the rules engine treats as LAMINATED, mirroring `ensemble.laminatedAreas`
 * in `services/api/internal/ensemble/run.go`.
 *
 * It is two, not one: the roof is laminated on most modern panoramic and sunroof glass,
 * so it gets a repair path too. Everything else is tempered, where any confirmed damage
 * is a replacement — `decision.go` reads `TemperedDamageDetected` and nothing else.
 *
 * Kept as an explicit table for the same reason the server keeps one: getting it wrong is
 * not cosmetic. Adding glazing means editing `GlassAreas`, `ensemble.areaKind` AND this.
 */
const LAMINATED: ReadonlySet<GlassArea> = new Set<GlassArea>(['FRONT_WINDSHIELD', 'ROOF_GLASS']);

export function isLaminated(area: GlassArea): boolean {
  return LAMINATED.has(area);
}
